/**
 * Organization Approval Modal
 * 사업자 승인/거부 모달
 */

import React, { useState } from 'react';
import { Organization } from '../../models';

interface OrganizationApprovalModalProps {
  organization: Organization;
  mode: 'approve' | 'reject';
  onClose: () => void;
  onConfirm: (reason?: string) => void;
  loading: boolean;
}

export const OrganizationApprovalModal: React.FC<
  OrganizationApprovalModalProps
> = ({ organization, mode, onClose, onConfirm, loading }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = () => {
    setError('');

    if (mode === 'reject') {
      if (!reason.trim()) {
        setError('거부 사유를 입력해주세요.');
        return;
      }
      if (reason.length < 10) {
        setError('거부 사유는 10자 이상 입력해주세요.');
        return;
      }
    }

    onConfirm(reason || undefined);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b ${
            mode === 'approve'
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <h2
            className={`text-lg font-semibold ${
              mode === 'approve' ? 'text-green-900' : 'text-red-900'
            }`}
          >
            {mode === 'approve' ? '✅ 사업자 승인' : '❌ 사업자 거부'}
          </h2>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          <div className="mb-4">
            <p className="text-gray-700 mb-2">
              <strong>{organization.name}</strong>
              {mode === 'approve' ? '을(를) 승인하시겠습니까?' : '을(를) 거부하시겠습니까?'}
            </p>
            <div className="text-sm text-gray-600 space-y-1">
              {organization.business_number && (
                <p>사업자번호: {organization.business_number}</p>
              )}
              {organization.phone && <p>전화번호: {organization.phone}</p>}
              {organization.address && <p>주소: {organization.address}</p>}
            </div>
          </div>

          {/* Reason Input (for rejection) */}
          {mode === 'reject' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                거부 사유 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="거부 사유를 상세히 입력해주세요 (최소 10자)"
              />
              <p className="mt-1 text-xs text-gray-500">
                사업자에게 전달되는 내용입니다.
              </p>
            </div>
          )}

          {/* Reason Input (optional for approval) */}
          {mode === 'approve' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                승인 메모 (선택사항)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                placeholder="내부 메모를 입력하세요"
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            className={`px-4 py-2 rounded-md text-white disabled:opacity-50 ${
              mode === 'approve'
                ? 'bg-green-600 hover:bg-green-700'
                : 'bg-red-600 hover:bg-red-700'
            }`}
          >
            {loading
              ? '처리 중...'
              : mode === 'approve'
                ? '승인'
                : '거부'}
          </button>
        </div>
      </div>
    </div>
  );
};
